import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ApiError, api, idempotencyKey } from '@/lib/api';
import { optionLetter } from '@/lib/format';
import { useToast } from '@/components/Toast';
import { FullPageSpinner } from '@/components/Skeleton';
import type { Attempt, AttemptResult, Today } from '@/lib/types';

/**
 * FE-3 — taking the check-in. Designed mobile-first: this is the screen a participant
 * uses on a phone at the end of the day.
 *
 * Three rules dominate this file:
 *
 *  • **Render options exactly as the API returns them (FE-3-19).** They are already
 *    shuffled per attempt (R10). Sorting or re-ordering them client-side — even
 *    incidentally, via a re-render that rebuilds the array — would detach a saved
 *    answer from the row the participant clicked.
 *
 *  • **Autosave every selection (FE-3-22).** Closing the tab mid-quiz must resume at
 *    the right question with an identical layout.
 */
export function QuizPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [index, setIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  /** FE-3-23 — generated once per mounted attempt, so a retry is a retry. */
  const submitKey = useRef(idempotencyKey());
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Local echo of saved answers, so the UI responds instantly while the PATCH flies.
  const [local, setLocal] = useState<
    Record<string, { chosenOptionId: string | null; notApplicable: boolean }>
  >({});

  const today = useQuery({ queryKey: ['today'], queryFn: () => api<Today>('/me/today') });

  const attempt = useQuery({
    queryKey: ['attempt'],
    // Idempotent on the server: returns the in-progress attempt if one exists.
    queryFn: () => api<Attempt>('/me/attempts', { method: 'POST' }),
    enabled: today.data?.state === 'available' || today.data?.state === 'in_progress',
    retry: false,
    staleTime: Infinity,
    // Refetching would be harmless (same shuffle) but pointless — and a refetch mid
    // quiz would reset scroll position.
    refetchOnWindowFocus: false,
  });

  // Seed local state from whatever the server already has (resume case).
  useEffect(() => {
    if (!attempt.data) return;

    const restored: typeof local = {};
    let firstUnanswered = -1;

    attempt.data.questions.forEach((question, i) => {
      if (question.answer) {
        restored[question.questionId] = question.answer;
      } else if (firstUnanswered === -1) {
        firstUnanswered = i;
      }
    });

    setLocal(restored);
    // Resume at the first unanswered question; if all are answered, land on the last
    // so the Submit button is in reach.
    setIndex(firstUnanswered === -1 ? attempt.data.questions.length - 1 : firstUnanswered);
  }, [attempt.data]);

  const saveAnswer = useMutation({
    mutationFn: (input: {
      attemptId: string;
      questionId: string;
      chosenOptionId: string | null;
      notApplicable: boolean;
    }) =>
      api<{ answeredCount: number; totalQuestions: number }>(
        `/me/attempts/${input.attemptId}/answers`,
        {
          method: 'PATCH',
          body: input.notApplicable
            ? { questionId: input.questionId, notApplicable: true }
            : { questionId: input.questionId, chosenOptionId: input.chosenOptionId },
        },
      ),
  });

  const submit = useMutation({
    mutationFn: (attemptId: string) =>
      api<AttemptResult>(`/me/attempts/${attemptId}/submit`, {
        method: 'POST',
        headers: { 'idempotency-key': submitKey.current },
      }),
  });

  const questions = attempt.data?.questions ?? [];
  const current = questions[index];
  const total = questions.length;

  const answeredCount = useMemo(
    () => questions.filter((q) => local[q.questionId] !== undefined).length,
    [questions, local],
  );

  const currentAnswer = current ? local[current.questionId] : undefined;
  const hasAnswered = currentAnswer !== undefined;
  const isLast = index === total - 1;
  const allAnswered = answeredCount === total && total > 0;

  // Move focus to the question heading on change, so a screen reader announces the
  // new question instead of leaving focus on a button that has just been replaced.
  useEffect(() => {
    headingRef.current?.focus();
  }, [index]);

  const choose = useCallback(
    (payload: { chosenOptionId: string | null; notApplicable: boolean }) => {
      if (!current || !attempt.data) return;

      setLocal((previous) => ({ ...previous, [current.questionId]: payload }));
      setSaving(true);

      saveAnswer.mutate(
        { attemptId: attempt.data.attemptId, questionId: current.questionId, ...payload },
        {
          onError: (cause) => {
            // Roll the optimistic choice back — leaving it on screen would tell the
            // participant their answer is safe when it is not.
            setLocal((previous) => {
              const next = { ...previous };
              delete next[current.questionId];
              return next;
            });
            toast.error(
              cause instanceof ApiError
                ? `Couldn't save that answer: ${cause.message}`
                : "Couldn't save that answer. Check your connection and try again.",
            );
          },
          onSettled: () => setSaving(false),
        },
      );
    },
    [current, attempt.data, saveAnswer, toast],
  );

  const goNext = useCallback(() => {
    if (!isLast) setIndex((i) => Math.min(i + 1, total - 1));
  }, [isLast, total]);

  const goBack = useCallback(() => setIndex((i) => Math.max(i - 1, 0)), []);

  const handleSubmit = useCallback(() => {
    if (!attempt.data || submit.isPending) return;

    setSubmitError(null);
    submit.mutate(attempt.data.attemptId, {
      onSuccess: (result) => {
        // Drop the cached today/dashboard so the next render reflects the submission.
        void queryClient.invalidateQueries({ queryKey: ['today'] });
        void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        queryClient.removeQueries({ queryKey: ['attempt'] });
        navigate(`/results/${result.attemptId}`, { replace: true, state: { result } });
      },
      onError: (cause) => {
        setSubmitError(
          cause instanceof ApiError ? cause.message : 'Could not submit. Please try again.',
        );
      },
    });
  }, [attempt.data, submit, queryClient, navigate]);

  // FE-3-18 — keyboard path: A–D or 1–4 select, Enter advances or submits.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (!current || submit.isPending) return;
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;

      const key = event.key.toLowerCase();

      const letterIndex = ['a', 'b', 'c', 'd'].indexOf(key);
      const numberIndex = ['1', '2', '3', '4'].indexOf(key);
      const pick = letterIndex !== -1 ? letterIndex : numberIndex;

      if (pick !== -1 && current.options[pick]) {
        event.preventDefault();
        choose({ chosenOptionId: current.options[pick].id, notApplicable: false });
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        if (isLast && allAnswered) handleSubmit();
        else if (hasAnswered) goNext();
        return;
      }

      if (event.key === 'ArrowRight' && hasAnswered && !isLast) goNext();
      if (event.key === 'ArrowLeft' && index > 0) goBack();
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [
    current,
    choose,
    isLast,
    allAnswered,
    hasAnswered,
    handleSubmit,
    goNext,
    goBack,
    index,
    submit.isPending,
  ]);

  // ── non-quiz states ──

  if (today.isLoading) return <FullPageSpinner label="Checking today's check-in…" />;

  if (today.data && today.data.state !== 'available' && today.data.state !== 'in_progress') {
    return <NotAvailable today={today.data} />;
  }

  if (attempt.isLoading) return <FullPageSpinner label="Preparing your questions…" />;

  if (attempt.isError) {
    return (
      <CenteredCard title="We couldn't start your check-in">
        <p className="text-sm text-muted">
          {attempt.error instanceof ApiError
            ? attempt.error.message
            : 'Something went wrong. Please try again.'}
        </p>
        <button type="button" onClick={() => void attempt.refetch()} className="btn-primary mt-5">
          Try again
        </button>
      </CenteredCard>
    );
  }

  if (!current) return <FullPageSpinner label="Loading…" />;

  const progressPct = Math.round((answeredCount / total) * 100);

  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      {/* ── progress header ── */}
      <header className="sticky top-0 z-10 border-b border-line bg-surface/95 backdrop-blur">
        <div className="mx-auto max-w-2xl px-4 py-3 sm:px-6">
          <div className="mb-2 flex items-center justify-between gap-3">
            <span className="font-mono tnum text-sm font-semibold text-ink">
              Q{index + 1} <span className="text-muted">/ {total}</span>
            </span>

            <span className="flex items-center gap-3">
              <span
                className="font-mono tnum text-xs text-muted"
                aria-live="polite"
                aria-atomic="true"
              >
                {saving ? 'Saving…' : `${answeredCount} answered`}
              </span>
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="text-xs font-medium text-muted hover:text-ink"
              >
                Finish later
              </button>
            </span>
          </div>

          <div
            className="h-1.5 w-full overflow-hidden rounded-full bg-surface-raised"
            role="progressbar"
            aria-valuenow={answeredCount}
            aria-valuemin={0}
            aria-valuemax={total}
            aria-label="Questions answered"
          >
            <div
              className="h-full rounded-full bg-accent transition-[width] duration-300"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      </header>

      {/* ── question ── */}
      {/* pb leaves room for the sticky footer, which would otherwise sit over the
          last few lines of content on a short viewport. */}
      <main id="main" className="mx-auto w-full max-w-2xl flex-1 px-4 pb-28 pt-6 sm:px-6 sm:pt-8">
        <p className="mb-2 text-xs uppercase tracking-wider text-muted">
          {attempt.data?.quiz.title ?? 'Check-in'}
        </p>

        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-lg font-semibold leading-snug text-ink outline-none sm:text-xl"
        >
          {current.text}
        </h1>

        {/*
          FE-3-19 — `current.options` is rendered in the order received. There is no
          sort, no reverse, no re-key by anything other than the option id.
        */}
        <div
          className="mt-6 space-y-2.5"
          role="radiogroup"
          aria-labelledby={undefined}
          aria-label="Answer options"
        >
          {current.options.map((option, optionIndex) => {
            const selected = currentAnswer?.chosenOptionId === option.id;
            return (
              <button
                key={option.id}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => choose({ chosenOptionId: option.id, notApplicable: false })}
                className={clsx(
                  'flex w-full items-start gap-3 rounded-control border px-4 py-3.5 text-left transition-colors',
                  selected
                    ? 'border-accent bg-accent-soft'
                    : 'border-line bg-surface hover:border-muted',
                )}
              >
                <span
                  className={clsx(
                    'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md font-mono text-xs font-semibold',
                    selected ? 'bg-accent text-[#0F1319]' : 'bg-surface-raised text-muted',
                  )}
                  aria-hidden="true"
                >
                  {optionLetter(optionIndex + 1)}
                </span>
                <span className="text-sm leading-relaxed text-ink">{option.text}</span>
              </button>
            );
          })}
        </div>

        {submitError && (
          <div
            className="mt-5 rounded-control border border-[#e0796b]/45 bg-[#e0796b]/12 px-3.5 py-3 text-sm text-ink"
            role="alert"
          >
            {submitError}
          </div>
        )}
      </main>

      {/* ── footer nav ── */}
      <footer className="sticky bottom-0 border-t border-line bg-surface/95 backdrop-blur">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <button
            type="button"
            onClick={goBack}
            disabled={index === 0}
            className="btn-secondary"
          >
            Back
          </button>

          <span className="hidden font-mono text-[11px] text-muted sm:block">
            A–D to choose · Enter to continue
          </span>

          {isLast ? (
            <button
              type="button"
              onClick={handleSubmit}
              // FE-3-23 — disabled while in flight, so a double tap cannot double submit.
              disabled={!allAnswered || submit.isPending}
              className="btn-primary"
            >
              {submit.isPending ? 'Submitting…' : 'Submit check-in'}
            </button>
          ) : (
            <button
              type="button"
              onClick={goNext}
              // FE-3-18 — Next stays disabled until a choice is made.
              disabled={!hasAnswered}
              className="btn-primary"
            >
              Next
            </button>
          )}
        </div>
      </footer>
    </div>
  );
}

function NotAvailable({ today }: { today: Today }) {
  const navigate = useNavigate();

  const heading =
    today.state === 'already_done'
      ? "You're done for today"
      : today.state === 'caught_up'
        ? "You're all caught up"
        : 'No check-in available yet';

  return (
    <CenteredCard title={heading}>
      <p className="text-sm leading-relaxed text-muted">{today.message}</p>
      {today.state === 'caught_up' && (
        <p className="mt-2 font-mono tnum text-xs text-muted">
          {today.progress.completed} of {today.progress.latestPublishedSequenceNo} completed
        </p>
      )}
      <button type="button" onClick={() => navigate('/dashboard')} className="btn-primary mt-5">
        Back to dashboard
      </button>
    </CenteredCard>
  );
}

function CenteredCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4">
      <main id="main" className="card w-full max-w-md p-7 text-center">
        <h1 className="text-base font-semibold text-ink">{title}</h1>
        <div className="mt-2">{children}</div>
      </main>
    </div>
  );
}
